import { AdminProfessionals } from '@platform/client';
import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { CheckBox, Modal, ModalCallToAction } from '@platform/components';

type DisableUserStage = 'confirmation' | 'submitting' | 'error' | 'success';

export const DisableUserModal = (props: {
  userId: number;
  onClose: () => void;
  isOpen: boolean;
}) => {
  const [modalStage, setModalStage] = useState<DisableUserStage>('confirmation');
  const [userConfirmed, setUserConfirmed] = useState(false);
  const handleClose = () => {
    setModalStage('confirmation');
    props.onClose();
  };
  const queryClient = useQueryClient();
  const disableUserMutation = AdminProfessionals.useDisableProUser({
    mutation: {
      onMutate: () => {
        setModalStage('submitting');
      },
      onSuccess: () => {
        setModalStage('success');
        queryClient.invalidateQueries({
          queryKey: [...AdminProfessionals.getGetProfessionalReviewDataQueryKey(props.userId)],
        });
      },
      onError: () => {
        setModalStage('error');
      },
    },
  });
  const primaryCTA: ModalCallToAction = {
    text: modalStage === 'confirmation' ? 'Disable user' : 'Close',
    buttonVariant: modalStage === 'confirmation' ? 'primary-danger' : 'secondary-gray',
    callback: () => {
      if (modalStage === 'confirmation') {
        disableUserMutation.mutate({ userId: props.userId });
      } else {
        handleClose();
      }
    },
  };
  const secondaryCTA: ModalCallToAction | undefined = {
    text: 'Cancel',
    callback: handleClose,
  };
  return (
    <Modal
      title='Disable account'
      variant='dialog'
      isOpen={props.isOpen}
      headerDivision='divider'
      onClose={handleClose}
      primaryCTA={{
        ...primaryCTA,
        isDisabled: () => modalStage === 'confirmation' && !userConfirmed,
      }}
      secondaryCTA={modalStage === 'confirmation' ? secondaryCTA : undefined}
      ignoreExtraPadding
    >
      <div className='w-full'>
        {modalStage === 'confirmation' && (
          <ConfirmDisableUser setUserConfirmed={setUserConfirmed} />
        )}
        {modalStage === 'error' && <ErrorContent />}
        {modalStage === 'success' && <SuccessDisabledContent />}
      </div>
    </Modal>
  );
};

const ConfirmDisableUser = ({
  setUserConfirmed,
}: {
  setUserConfirmed: (value: boolean) => void;
}) => {
  return (
    <div className='flex flex-col gap-4'>
      <p>This blocks the user from accessing the platform and removes their match data.</p>
      <p>Removing personal data is a separate tech team ask.</p>
      <p className='text-error-500'>Match data is deleted and cannot be restored.</p>
      <CheckBox
        description="I understand that this user's match data will be permanently deleted."
        size='sm'
        onChange={(checked) => setUserConfirmed(checked)}
      />
    </div>
  );
};

const SuccessDisabledContent = () => {
  return <p>The user has been successfully disabled.</p>;
};

const ErrorContent = () => {
  return <p>Sorry, there was an error processing your request. Please try again.</p>;
};
